import { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'

// The two ad placements managed in the mefarsemim admin (mekome-admin:
// src/pages/app/advertise/adPlacements.ts). Ids must match on both sides.
export type AdPlacementId = 'top_banner' | 'coupon_card'

export interface ServedAd {
  id: string
  name: string
  imageUrl: string
  clickUrl: string
  width: number
  height: number
  // top_banner: rotation order; coupon_card: 1-based position in the coupon grid.
  order: number
  // top_banner only: seconds before the next ad replaces this one.
  durationSec: number | null
}

interface ServeAdResponse {
  ads: ServedAd[]
  requestId: string
}

const AD_FUNCTIONS_URL = 'https://us-central1-mefarsemim-net.cloudfunctions.net'
const DEFAULT_DURATION_SEC = 6

// Fetches every live ad of a placement (already sorted by order) plus a click tracker.
export const useServedAds = (placement: AdPlacementId) => {
  const [ads, setAds] = useState<ServedAd[]>([])
  const [requestId, setRequestId] = useState('')

  useEffect(() => {
    let cancelled = false
    fetch(`${AD_FUNCTIONS_URL}/serveAd?placement=${placement}&platform=web`)
      .then(res => res.ok ? res.json() : Promise.reject(res.status))
      .then((data: ServeAdResponse) => {
        if (cancelled) return
        setAds((data?.ads ?? []).filter(ad => ad.imageUrl))
        setRequestId(data?.requestId ?? '')
      })
      .catch(() => { /* no ads on error */ })
    return () => { cancelled = true }
  }, [placement])

  // Logs the click for the admin dashboard (adClicks). Fire-and-forget; the link opens in a
  // new tab, so this page stays alive for the request to finish.
  const trackClick = useCallback((ad: ServedAd) => {
    fetch(`${AD_FUNCTIONS_URL}/clickAd`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ placement, adId: ad.id, requestId, platform: 'web' }),
    }).catch(() => {})
  }, [placement, requestId])

  return { ads, trackClick }
}

const BannerWrapper = styled.a<{ hasLink: boolean }>`
  display: block;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  cursor: ${p => p.hasLink ? 'pointer' : 'default'};
  flex-shrink: 0;
  text-decoration: none;
`

const BannerImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
`

// Top banner: shows the live ads one at a time in their admin order, each for its durationSec.
const AdBanner: React.FC = () => {
  const { ads, trackClick } = useServedAds('top_banner')
  const [index, setIndex] = useState(0)

  const ad = ads.length ? ads[index % ads.length] : null

  useEffect(() => {
    if (!ad || ads.length < 2) return
    const timer = setTimeout(() => setIndex(i => (i + 1) % ads.length), (ad.durationSec || DEFAULT_DURATION_SEC) * 1000)
    return () => clearTimeout(timer)
  }, [ad, ads.length])

  if (!ad) return null

  return (
    <BannerWrapper
      href={ad.clickUrl || undefined}
      target={ad.clickUrl ? '_blank' : undefined}
      rel={ad.clickUrl ? 'noopener noreferrer' : undefined}
      hasLink={!!ad.clickUrl}
      onClick={e => (ad.clickUrl ? trackClick(ad) : e.preventDefault())}
    >
      <BannerImage key={ad.id} src={ad.imageUrl} alt={ad.name} />
    </BannerWrapper>
  )
}

// Slots each coupon_card ad into the list so it lands at its `order` (1 = first card). Ads whose
// position is past the end of the list go last.
export function withCardAds<T>(coupons: T[], ads: ServedAd[]): Array<{ coupon: T } | { ad: ServedAd }> {
  const out: Array<{ coupon: T } | { ad: ServedAd }> = []
  let ci = 0
  let ai = 0
  while (ci < coupons.length || ai < ads.length) {
    if (ai < ads.length && (ads[ai].order <= out.length + 1 || ci >= coupons.length)) {
      out.push({ ad: ads[ai++] })
    } else {
      out.push({ coupon: coupons[ci++] })
    }
  }
  return out
}

export default AdBanner
